<template>
  <ConfirmDialog :style="{ width: '440px' }" />
  <StepShell
    :title="t(isShopify ? 'completeV2.steps.connect.screenTitleShopify' : 'completeV2.steps.connect.screenTitle')"
    :intro="t(isShopify ? 'completeV2.steps.connect.screenIntroShopify' : 'completeV2.steps.connect.screenIntro')"
    :can-go-back="canGoBack"
    :continue-disabled="!connected"
    @next="$emit('next')"
    @back="$emit('back')"
  >
    <div class="connect-step">
      <!-- ====== MIGRATION TIP (platform migration flow) ======
           Surfaced when the parent flow is the platform migration
           (cf. MigrationV5.vue). Reminds the user to uninstall the
           previous version of the module before installing the new
           one, to avoid conflicts on credentials / CRON / DB
           triggers. Intentionally toned down (info, not danger) — the
           consequence is recoverable.
           NON pertinent pour Shopify : la connexion est automatique (POST
           /shopify/connect), il n'y a aucun module à désinstaller/réinstaller
           — d'où le garde `!isShopify`, cohérent avec les autres sections. -->
      <aside
        v-if="showMigrationWarning && !isShopify"
        class="connect-step__migration-warning"
      >
        <i class="fa-regular fa-circle-info connect-step__migration-warning-icon" />
        <div class="connect-step__migration-warning-body">
          <strong class="connect-step__migration-warning-title">
            {{ t('completeV2.steps.connect.migrationWarning.title') }}
          </strong>
          <p class="connect-step__migration-warning-text">
            {{ t('completeV2.steps.connect.migrationWarning.body') }}
          </p>
          <ol class="connect-step__migration-warning-steps">
            <li>{{ t('completeV2.steps.connect.migrationWarning.step1') }}</li>
            <li>{{ t('completeV2.steps.connect.migrationWarning.step2') }}</li>
            <li>{{ t('completeV2.steps.connect.migrationWarning.step3') }}</li>
          </ol>
        </div>
      </aside>

      <!-- ====== PLATFORM CARD (with inline instructions link) ====== -->
      <section
        v-if="platformMeta && !isShopify"
        class="connect-step__platform"
      >
        <div class="connect-step__platform-row">
          <div class="connect-step__platform-main">
            <div class="connect-step__platform-icon-wrap">
              <img
                v-if="platformIconSrc"
                :src="platformIconSrc"
                :alt="platformMeta.name"
                class="connect-step__platform-icon"
                @error="iconBroken = true"
              >
              <i
                v-else
                class="far fa-store connect-step__platform-icon connect-step__platform-icon--fallback"
              />
            </div>
            <div class="connect-step__platform-info">
              <span class="connect-step__platform-name">{{ platformMeta.name }}</span>
              <button
                v-if="!isShopify && instructions.length"
                type="button"
                class="connect-step__instructions-link"
                :aria-expanded="instructionsOpen"
                @click="instructionsOpen = !instructionsOpen"
              >
                <i :class="instructionsOpen ? 'far fa-chevron-up' : 'far fa-chevron-down'" />
                <span>
                  {{ instructionsOpen
                    ? t('completeV2.steps.connect.instructionsToggleHide')
                    : t('completeV2.steps.connect.instructionsToggleShow') }}
                </span>
              </button>
            </div>
          </div>
          <div class="connect-step__platform-actions">
            <a
              v-if="platformMeta.downloadLink"
              v-tooltip.top="!canDownloadModule ? t('completeV2.steps.connect.comingSoon') : ''"
              :href="canDownloadModule ? platformMeta.downloadLink : undefined"
              :target="canDownloadModule ? '_blank' : undefined"
              rel="noopener"
              class="connect-step__download-btn"
              :class="{ 'connect-step__download-btn--disabled': !canDownloadModule }"
              :aria-disabled="!canDownloadModule ? 'true' : undefined"
              @click="!canDownloadModule && $event.preventDefault()"
            >
              <i class="far fa-arrow-down-to-line" />
              <span>{{ t('completeV2.steps.connect.downloadModule') }}</span>
            </a>
            <!-- TODO: réactiver lorsqu'on autorisera la modification de la solution e-commerce après inscription -->
            <!-- <button
              type="button"
              class="connect-step__platform-edit"
              v-tooltip.top="t('completeV2.steps.connect.editPlatform')"
              :aria-expanded="editorOpen"
              @click="editorOpen = !editorOpen"
            >
              <i :class="editorOpen ? 'far fa-xmark' : 'far fa-pen-to-square'" />
            </button> -->
          </div>
        </div>

        <transition name="editor">
          <div
            v-if="instructionsOpen && !isShopify && instructions.length"
            class="connect-step__instructions-panel"
          >
            <ol class="connect-step__instructions-list">
              <li
                v-for="(instruction, index) in instructions"
                :key="index"
                class="connect-step__instructions-item"
              >
                <span class="connect-step__instructions-bullet">{{ index + 1 }}</span>
                <div
                  class="connect-step__instructions-text"
                  v-html="t(instruction)"
                />
              </li>
            </ol>
          </div>
        </transition>
      </section>

      <transition name="editor">
        <div
          v-if="editorOpen && !isShopify"
          class="connect-step__editor"
        >
          <div class="connect-step__editor-title">
            {{ t('completeV2.steps.connect.editorTitle') }}
          </div>
          <div class="connect-step__editor-grid">
            <button
              v-for="p in availablePlatforms"
              :key="p.id"
              type="button"
              class="connect-step__editor-tile"
              :class="{
                'connect-step__editor-tile--active': draftPlatform === p.id,
                'connect-step__editor-tile--disabled': p.disabled,
              }"
              :disabled="p.disabled"
              @click="!p.disabled && (draftPlatform = p.id)"
            >
              <img
                v-if="p.image"
                :src="p.image"
                :alt="p.label"
                class="connect-step__editor-tile-icon connect-step__editor-tile-icon--img"
              >
              <i
                v-else
                class="connect-step__editor-tile-icon"
                :class="p.icon"
              />
              <span class="connect-step__editor-tile-label">{{ p.label }}</span>
              <span
                v-if="p.disabled"
                class="connect-step__editor-tile-badge"
              >
                {{ t('completeV2.steps.connect.comingSoon') }}
              </span>
            </button>
          </div>
          <div class="connect-step__editor-actions">
            <SpmButton
              :label="t('completeV2.backCta')"
              class-style="p-button p-button-text p-button-secondary"
              @click="cancelEditor"
            />
            <SpmButton
              :label="t('completeV2.steps.connect.editorConfirm')"
              class-style="p-button p-button-success"
              :disabled="!draftPlatform || draftPlatform === platform || savingPlatform"
              :loading="savingPlatform"
              @click="confirmPlatform"
            />
          </div>
        </div>
      </transition>

      <!-- ====== STATUS (prominent, centered) ====== -->
      <div
        class="connect-step__status"
        :class="{ 'connect-step__status--ok': connected }"
        role="status"
        aria-live="polite"
      >
        <template v-if="connected">
          <div class="connect-step__status-indicator connect-step__status-indicator--ok">
            <i class="far fa-circle-check" />
          </div>
          <div class="connect-step__status-body">
            <div class="connect-step__status-title">
              {{ t('completeV2.steps.connect.connected') }}
            </div>
          </div>
        </template>
        <template v-else>
          <div class="connect-step__status-indicator">
            <span class="connect-step__pulse-dot" />
            <span class="connect-step__pulse-ring connect-step__pulse-ring--1" />
            <span class="connect-step__pulse-ring connect-step__pulse-ring--2" />
          </div>
          <div class="connect-step__status-body">
            <div class="connect-step__status-title">
              {{ t('completeV2.steps.connect.waiting') }}
            </div>
            <p class="connect-step__status-help">
              {{ t('completeV2.steps.connect.waitingHelp') }}
            </p>
          </div>
        </template>
      </div>

      <!-- ====== SHOPIFY VARIANT (auto-provisioning) ====== -->
      <section
        v-if="isShopify"
        class="connect-step__shopify"
      >
        <div class="connect-step__shopify-head">
          <i class="fab fa-shopify connect-step__shopify-icon" />
          <div>
            <div class="connect-step__shopify-title">
              {{ t('completeV2.steps.connect.shopifyConnectingTitle') }}
            </div>
            <p class="connect-step__shopify-text">
              {{ t('completeV2.steps.connect.shopifyConnectingText') }}
            </p>
          </div>
        </div>
        <div
          v-if="shopifyError"
          class="connect-step__warning-inline"
          role="alert"
        >
          <i class="far fa-triangle-exclamation" />
          <span>{{ shopifyError }}</span>
          <button
            type="button"
            class="connect-step__shopify-retry"
            :disabled="shopifyConnecting"
            @click="connectShopify"
          >
            <i :class="shopifyConnecting ? 'far fa-spinner-third fa-spin' : 'far fa-arrows-rotate'" />
            <span>{{ t('completeV2.steps.connect.shopifyRetry') }}</span>
          </button>
        </div>
      </section>

      <!-- ====== API KEYS (compact) ====== -->
      <section
        v-if="!isShopify"
        class="connect-step__keys-card"
      >
        <header class="connect-step__keys-header">
          <span class="connect-step__section-icon connect-step__section-icon--key">
            <i class="far fa-key" />
          </span>
          <h3 class="connect-step__section-title">
            {{ t('completeV2.steps.connect.apiKeysTitle') }}
          </h3>
          <span class="connect-step__keys-hint">{{ t('completeV2.steps.connect.apiKeysIntroShort') }}</span>
        </header>

        <div
          v-if="keyIsRevealed"
          class="connect-step__warning-inline"
          role="note"
        >
          <i class="far fa-triangle-exclamation" />
          <span>{{ t('completeV2.steps.connect.apiKeyWarningShort') }}</span>
        </div>

        <div
          v-else-if="!keysLoading"
          class="connect-step__warning-inline connect-step__warning-inline--muted"
          role="note"
        >
          <i class="far fa-lock" />
          <span>{{ t('completeV2.steps.connect.apiKeyHiddenNote') }}</span>
        </div>

        <div
          v-if="keysLoading"
          class="connect-step__keys-loading"
        >
          <i class="far fa-spinner-third fa-spin" />
          {{ t('completeV2.steps.connect.apiKeyLoading') }}
        </div>

        <div
          v-else
          class="connect-step__keys"
        >
          <div
            v-for="key in apiKeys"
            :key="key.id"
            class="connect-step__key"
          >
            <div class="connect-step__key-label">
              {{ key.label }}
            </div>
            <div
              class="connect-step__key-row"
              :class="{ 'connect-step__key-row--copied': copiedKeyId === key.id }"
            >
              <code class="connect-step__key-value">{{ key.value || '—' }}</code>
              <button
                type="button"
                class="connect-step__key-copy"
                :disabled="!key.value || (key.id === 'apiKey' && !keyIsRevealed)"
                :aria-label="t('completeV2.steps.connect.copy')"
                @click="copy(key.value, key.id)"
              >
                <i :class="copiedKeyId === key.id ? 'far fa-check' : 'far fa-clipboard'" />
              </button>
            </div>
          </div>
        </div>

        <div class="connect-step__keys-footer">
          <button
            v-tooltip.top="keyIsRevealed ? t('completeV2.steps.connect.regenerateLockedHint') : ''"
            type="button"
            class="connect-step__regenerate-link"
            :disabled="regenerating || keysLoading || keyIsRevealed"
            @click="regenerateKey"
          >
            <i
              :class="regenerating ? 'far fa-spinner-third fa-spin' : 'far fa-arrows-rotate'"
            />
            <span>{{ t('completeV2.steps.connect.regenerateCta') }}</span>
          </button>
        </div>
      </section>

    </div>
  </StepShell>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import { getEcommerceModule, getEcommerceModulesList, checkIfShopConnected } from '@/configs/complete';
import { updateShop } from '@/composables/shop/Shops';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { Me, UserState } from '@/composables/User';
import { showToastError, showToastSuccess } from '@/helpers';

const POLL_INTERVAL_MS = 5000;
const ENABLED_PLATFORM_IDS = new Set(['prestashop', 'magento', 'magento2', 'woocommerce']);

export default defineComponent({
  name: 'ConnectStep',

  components: {
    StepShell,
    SpmButton,
    ConfirmDialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
    /**
     * When true, displays a prominent warning banner at the top of the
     * step explaining that the user MUST uninstall the legacy V4
     * Shopimind module before installing the V5 one. Used by the V4 → V5
     * migration flow (`MigrationV5.vue`). Kept off by default so the
     * regular onboarding for fresh shops doesn't show a confusing
     * "uninstall first" instruction (there's nothing to uninstall).
     */
    showMigrationWarning: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();
    const confirm = useConfirm();
    const instructionsOpen = ref(false);

    const platform = computed(() => UserState.activeShop?.solutionEcommerce ?? '');
    const isShopify = computed(() => platform.value === 'shopify');
    const platformMeta = computed(() => (platform.value
      ? getEcommerceModule(platform.value)
      : null));

    const instructions = computed<string[]>(() => {
      const meta: any = platformMeta.value;
      if (!meta) return [];
      return [
        ...(meta.downloadInstructions ?? []),
        ...(meta.installationInstructions ?? []),
      ];
    });

    const iconBroken = ref(false);
    const platformIconSrc = computed(() => {
      if (iconBroken.value || !platform.value) return '';
      return `/images/ecommerce-solutions/${platform.value}.svg`;
    });

    const shopIdentifier = ref('');
    const apiKeyValue = ref('');
    const keysLoading = ref(false);
    const regenerating = ref(false);
    const keyIsRevealed = ref(false);
    const currentKey = ref<{ id: number; prefix: string } | null>(null);

    const apiKeys = computed(() => [
      {
        id: 'identifiant',
        label: t('completeV2.steps.connect.identifiantLabel'),
        value: shopIdentifier.value,
      },
      {
        id: 'apiKey',
        label: t('completeV2.steps.connect.apiKeyLabel'),
        value: apiKeyValue.value,
      },
    ]);

    const createKey = async () => {
      const shopId = UserState.activeShop?.id;
      if (!shopId) return;
      const created: any = await nestCall(
        'post',
        NestApiType.DEFAULT,
        `/shop/keys/${shopId}`,
        {},
        '',
        { key_name: 'Module' },
      );
      if (created?.success) {
        currentKey.value = {
          id: created.data?.id,
          prefix: created.data?.prefix ?? '',
        };
        apiKeyValue.value = created.data?.full_key ?? '';
        keyIsRevealed.value = true;
      }
    };

    const loadApiKeys = async () => {
      const shopId = UserState.activeShop?.id;
      if (!shopId) return;
      keysLoading.value = true;
      try {
        const existing: any = await nestCall('get', NestApiType.DEFAULT, `/shop/keys/${shopId}`);
        if (existing?.success) {
          shopIdentifier.value = existing.data?.identifiant ?? '';
          const keys: Array<{ id: number; prefix: string }> = (existing.data?.keys ?? [])
            .filter((k: { prefix: string }) => !k.prefix?.toLowerCase().startsWith('sys'));
          if (keys.length > 0) {
            currentKey.value = { id: keys[0].id, prefix: keys[0].prefix };
            apiKeyValue.value = `${keys[0].prefix}********************`;
            keyIsRevealed.value = false;
            return;
          }
        }
        await createKey();
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        keysLoading.value = false;
      }
    };

    const performRegenerate = async () => {
      const shopId = UserState.activeShop?.id;
      if (!shopId || !currentKey.value?.id) return;
      regenerating.value = true;
      try {
        await nestCall('delete', NestApiType.DEFAULT, `/shop/keys/${shopId}/${currentKey.value.id}`);
        await createKey();
        await showToastSuccess(t('completeV2.steps.connect.regenerated'));
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        regenerating.value = false;
      }
    };

    const regenerateKey = () => {
      if (!currentKey.value?.id) return;
      confirm.require({
        message: t('completeV2.steps.connect.regenerateConfirm'),
        header: t('completeV2.steps.connect.regenerateConfirmTitle'),
        icon: 'far fa-triangle-exclamation',
        acceptLabel: t('completeV2.steps.connect.regenerateAccept'),
        rejectLabel: t('completeV2.backCta'),
        acceptClass: 'p-button-danger',
        accept: () => {
          performRegenerate();
        },
      });
    };

    const editorOpen = ref(false);
    const draftPlatform = ref('');
    const savingPlatform = ref(false);

    const availablePlatforms = computed(() => getEcommerceModulesList().map((p) => ({
      ...p,
      disabled: p.disabled || !ENABLED_PLATFORM_IDS.has(p.id),
    })));

    const canDownloadModule = computed(() => Boolean(
      platform.value
      && ENABLED_PLATFORM_IDS.has(platform.value)
      && platformMeta.value?.downloadLink,
    ));

    const cancelEditor = () => {
      editorOpen.value = false;
      draftPlatform.value = platform.value;
    };

    const confirmPlatform = async () => {
      if (!draftPlatform.value || draftPlatform.value === platform.value) return;
      if (!UserState.activeShop?.id) return;
      savingPlatform.value = true;
      try {
        await updateShop([{
          id_shop: UserState.activeShop.id,
          solution_ecommerce: draftPlatform.value,
          date_modification: new Date().toISOString(),
        }]);
        await Me(true, UserState.activeShop.id);
        editorOpen.value = false;
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        savingPlatform.value = false;
      }
    };

    const connected = ref(false);
    const shopifyConnecting = ref(false);
    const shopifyError = ref<string | null>(null);
    let pollHandle: number | null = null;

    const connectShopify = async () => {
      if (shopifyConnecting.value) return;
      shopifyConnecting.value = true;
      shopifyError.value = null;
      try {
        const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/connect');
        if (!res?.success) {
          shopifyError.value = res?.message || t('completeV2.steps.connect.shopifyConnectError');
        }
      } catch (e: any) {
        shopifyError.value = e?.message || t('completeV2.steps.connect.shopifyConnectError');
      } finally {
        shopifyConnecting.value = false;
      }
    };

    const copiedKeyId = ref<string | null>(null);
    let copyResetHandle: number | null = null;

    const copy = async (value: string, keyId?: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        await showToastSuccess(t('completeV2.steps.connect.copied'));
        if (keyId) {
          copiedKeyId.value = keyId;
          if (copyResetHandle !== null) window.clearTimeout(copyResetHandle);
          copyResetHandle = window.setTimeout(() => {
            copiedKeyId.value = null;
            copyResetHandle = null;
          }, 1800);
        }
      } catch {
        await showToastError('GENERIC_ERROR');
      }
    };

    const poll = async () => {
      try {
        if (await checkIfShopConnected()) {
          connected.value = true;
          if (pollHandle !== null) {
            window.clearInterval(pollHandle);
            pollHandle = null;
          }
          emit('next');
        }
      } catch {
        /* noop */
      }
    };

    onMounted(async () => {
      draftPlatform.value = platform.value;
      if (await checkIfShopConnected()) {
        connected.value = true;
        emit('next');
        return;
      }

      if (platform.value && platform.value !== 'shopify' && UserState.activeShop?.id) {
        await loadApiKeys();
      }

      if (isShopify.value && UserState.activeShop?.id) {
        await connectShopify();
      }

      pollHandle = window.setInterval(poll, POLL_INTERVAL_MS);
    });

    onBeforeUnmount(() => {
      if (pollHandle !== null) {
        window.clearInterval(pollHandle);
        pollHandle = null;
      }
    });

    return {
      t,
      platform,
      platformMeta,
      instructions,
      instructionsOpen,
      platformIconSrc,
      iconBroken,
      isShopify,
      apiKeys,
      keysLoading,
      keyIsRevealed,
      regenerating,
      regenerateKey,
      connected,
      copy,
      copiedKeyId,
      editorOpen,
      draftPlatform,
      savingPlatform,
      availablePlatforms,
      canDownloadModule,
      cancelEditor,
      confirmPlatform,
      shopifyConnecting,
      shopifyError,
      connectShopify,
    };
  },
});
</script>

<style lang="scss" scoped>
.connect-step {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* ─── Migration info banner (platform migration flow) ─── */
  /* Intentionally toned down vs a warning style — the message is a
     reminder, not a danger callout. Soft blue/info palette. */
  &__migration-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 0.85rem 1rem;
    background: linear-gradient(135deg, rgba(96, 165, 250, 0.08) 0%, rgba(96, 165, 250, 0.03) 100%);
    border: 1px solid rgba(96, 165, 250, 0.3);
    border-left: 3px solid #60a5fa;
    border-radius: 0.6rem;
  }

  &__migration-warning-icon {
    flex-shrink: 0;
    font-size: 1.05rem;
    color: #2563eb;
    margin-top: 0.2rem;
  }

  &__migration-warning-body {
    flex: 1;
    min-width: 0;
  }

  &__migration-warning-title {
    display: block;
    color: #1e3a8a;
    font-weight: 600;
    font-size: 0.92rem;
    margin-bottom: 0.3rem;
  }

  &__migration-warning-text {
    margin: 0 0 0.5rem;
    color: #1e40af;
    font-size: 0.84rem;
    line-height: 1.5;
  }

  &__migration-warning-steps {
    margin: 0;
    padding-left: 1.2rem;
    color: #1e40af;
    font-size: 0.84rem;
    line-height: 1.55;

    li {
      margin: 0.2rem 0;
    }
  }

  /* ─── Section icon ─── */
  &__section-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.12);
    color: #5d8a24;
    font-size: 0.9rem;
    flex-shrink: 0;

    &--key {
      width: 2.1rem;
      height: 2.1rem;
      font-size: 0.95rem;
      background: linear-gradient(135deg, rgba(148, 200, 64, 0.2), rgba(93, 138, 36, 0.15));
    }
  }

  &__section-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }

  /* ─── Platform card ─── */
  &__platform {
    display: flex;
    flex-direction: column;
    padding: 0.75rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.7rem;
    background: linear-gradient(135deg, #ffffff, #f8fafc);
  }

  &__platform-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.85rem;
  }

  &__platform-main {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    flex: 1;
    min-width: 0;
  }

  &__instructions-link {
    margin-top: 0.2rem;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 500;
    color: #5d8a24;
    cursor: pointer;
    align-self: flex-start;

    i {
      font-size: 0.65rem;
    }

    &:hover {
      text-decoration: underline;
      color: #4f761d;
    }
  }

  &__platform-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.6rem;
    background: #ffffff;
    border: 1px solid #eef0f3;
    flex-shrink: 0;
  }

  &__platform-icon {
    width: 1.85rem;
    height: 1.85rem;
    object-fit: contain;

    &--fallback {
      width: auto;
      height: auto;
      font-size: 1.25rem;
      color: #94a3b8;
    }
  }

  &__platform-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
    min-width: 0;
  }

  &__platform-name {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
  }

  &__platform-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  &__download-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem 0.85rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #ffffff;
    background: linear-gradient(135deg, #94c840, #5d8a24);
    border: 1px solid #5d8a24;
    border-radius: 0.5rem;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: 0 1px 2px rgba(93, 138, 36, 0.25);
    white-space: nowrap;

    i {
      font-size: 0.85rem;
    }

    &:hover {
      background: linear-gradient(135deg, #85b639, #4f761d);
      box-shadow: 0 2px 6px rgba(93, 138, 36, 0.35);
      color: #ffffff;
      text-decoration: none;
    }

    &--disabled {
      background: #f1f5f9;
      border-color: #e2e8f0;
      color: #94a3b8;
      cursor: not-allowed;
      box-shadow: none;
      pointer-events: auto;

      &:hover {
        background: #f1f5f9;
        color: #94a3b8;
        box-shadow: none;
      }
    }
  }

  &__platform-edit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    font-size: 0.85rem;
    color: #475569;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #94c840;
      color: #5d8a24;
      background: #f5faeb;
    }
  }

  /* ─── Editor ─── */
  &__editor {
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    padding: 0.9rem;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__editor-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #0f172a;
  }

  &__editor-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  &__editor-tile {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.55rem;
    background: #ffffff;
    font-family: inherit;
    font-size: 0.85rem;
    color: #334155;
    cursor: pointer;
    transition: all 0.15s ease;
    min-width: 0;

    &:hover {
      border-color: #94c840;
    }

    &--active {
      border-color: #5d8a24;
      background: #f5faeb;
      color: #2d5016;
      font-weight: 600;
    }
  }

  &__editor-tile-icon {
    font-size: 1rem;
    color: #475569;
    flex-shrink: 0;

    &--img {
      width: 1.25rem;
      height: 1.25rem;
      object-fit: contain;
    }
  }

  &__editor-tile-label {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  &__editor-tile-badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    background: #eef2f7;
    color: #64748b;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    text-transform: none;
  }

  &__editor-tile--active &__editor-tile-icon { color: #5d8a24; }

  &__editor-tile--disabled {
    cursor: not-allowed;
    background: #f8fafc;
    color: #94a3b8;

    .connect-step__editor-tile-icon {
      filter: grayscale(0.7);
      opacity: 0.65;
    }

    &:hover { border-color: #e2e8f0; background: #f8fafc; }
  }

  &__editor-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.4rem;
  }

  /* ─── Shopify variant ─── */
  &__shopify {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.1rem;
    border: 1px solid #95bf47;
    background: linear-gradient(135deg, #f0f8e8, #ffffff 80%);
    border-radius: 0.75rem;
  }

  &__shopify-head {
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
  }

  &__shopify-icon {
    font-size: 1.85rem;
    color: #95bf47;
    flex-shrink: 0;
  }

  &__shopify-title {
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 0.2rem;
    font-size: 0.95rem;
  }

  &__shopify-text {
    margin: 0;
    font-size: 0.82rem;
    color: #475569;
    line-height: 1.5;
  }

  &__shopify-cta {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.55rem 0.95rem;
    background: #95bf47;
    color: #ffffff;
    font-weight: 600;
    font-size: 0.85rem;
    border-radius: 0.5rem;
    text-decoration: none;
    transition: background 0.15s ease;

    &:hover { background: #7fa739; color: #ffffff; }
  }

  &__shopify-retry {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.65rem;
    background: #ffffff;
    border: 1px solid #f5d97f;
    border-radius: 0.4rem;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    color: #78350f;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: #fff8e6;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  /* ─── Keys card (compact) ─── */
  &__keys-card {
    padding: 0.9rem 1rem 0.75rem;
    background: linear-gradient(180deg, #ffffff, #fafcfa);
    border: 1px solid #d4e7af;
    border-radius: 0.75rem;
    box-shadow: 0 2px 10px rgba(148, 200, 64, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
  }

  &__keys-header {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    flex-wrap: wrap;
  }

  &__keys-hint {
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.3;
  }

  &__warning-inline {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.5rem 0.75rem;
    background: linear-gradient(135deg, #fff8e6, #fffaf0);
    border: 1px solid #f5d97f;
    border-radius: 0.45rem;
    font-size: 0.75rem;
    color: #78350f;
    line-height: 1.4;

    i {
      color: #d97706;
      font-size: 0.9rem;
      flex-shrink: 0;
    }

    &--muted {
      background: #f8fafc;
      border-color: #e2e8f0;
      color: #475569;

      i {
        color: #64748b;
      }
    }
  }

  &__keys-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: -0.1rem;
  }

  &__regenerate-link {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.4rem;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    border-radius: 0.35rem;
    transition: color 0.15s ease, background 0.15s ease;

    i {
      font-size: 0.75rem;
    }

    &:hover:not(:disabled) {
      color: #5d8a24;
      background: #f5faeb;
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__keys-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    color: #5d8a24;
    font-size: 0.85rem;
  }

  &__keys {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  &__key-label {
    font-size: 0.68rem;
    font-weight: 700;
    color: #475569;
    margin-bottom: 0.3rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__key-row {
    display: flex;
    align-items: stretch;
    background: #ffffff;
    border: 1px solid #d4e7af;
    border-radius: 0.5rem;
    overflow: hidden;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &--copied {
      border-color: #94c840;
      box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
    }
  }

  &__key-value {
    flex: 1;
    padding: 0.55rem 0.8rem;
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 0.82rem;
    color: #0f172a;
    word-break: break-all;
    line-height: 1.4;
  }

  &__key-copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    background: #f5faeb;
    color: #5d8a24;
    border: none;
    border-left: 1px solid #d4e7af;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background: #94c840;
      color: #ffffff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  /* ─── Status (prominent) ─── */
  &__status {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.2rem;
    border-radius: 0.85rem;
    background: linear-gradient(135deg, #f5faeb, #ffffff 80%);
    border: 1px solid #d4e7af;
    color: #0f172a;
    box-shadow: 0 2px 12px rgba(148, 200, 64, 0.1);

    &--ok {
      background: linear-gradient(135deg, #ecfccb, #f5faeb);
      border-color: #94c840;
      box-shadow: 0 2px 14px rgba(148, 200, 64, 0.2);
    }
  }

  &__status-indicator {
    position: relative;
    width: 2.75rem;
    height: 2.75rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &--ok {
      background: #94c840;
      border-radius: 50%;
      color: #ffffff;
      font-size: 1.35rem;
      box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.2);
    }
  }

  &__pulse-dot {
    position: absolute;
    width: 0.85rem;
    height: 0.85rem;
    background: #94c840;
    border-radius: 50%;
    box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.22);
    z-index: 2;
  }

  &__pulse-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.35);
    animation: pulse-ring 2s ease-out infinite;

    &--2 {
      animation-delay: 1s;
    }
  }

  &__status-body {
    flex: 1;
    min-width: 0;
  }

  &__status-title {
    font-weight: 700;
    color: #0f172a;
    font-size: 0.95rem;
    line-height: 1.3;
  }

  &__status-help {
    margin: 0.2rem 0 0;
    font-size: 0.78rem;
    color: #475569;
    line-height: 1.45;
  }
}

@keyframes pulse-ring {
  0% { transform: scale(0.6); opacity: 0.75; }
  100% { transform: scale(1.8); opacity: 0; }
}

.editor-enter-active,
.editor-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease, max-height 0.25s ease;
  overflow: hidden;
}

.editor-enter-from,
.editor-leave-to {
  opacity: 0;
  transform: translateY(-4px);
  max-height: 0;
}

.editor-enter-to,
.editor-leave-from {
  opacity: 1;
  max-height: 30rem;
}

/* ─── Inline instructions (inside platform card) ─── */
.connect-step {
  &__instructions-panel {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px dashed #e2e8f0;
  }

  &__instructions-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
  }

  &__instructions-item {
    display: flex;
    gap: 0.7rem;
    align-items: flex-start;
  }

  &__instructions-bullet {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.15);
    border: 1px solid #94c840;
    color: #5d8a24;
    font-size: 0.7rem;
    font-weight: 700;
    margin-top: 0.1rem;
  }

  &__instructions-text {
    flex: 1;
    font-size: 0.82rem;
    color: #475569;
    line-height: 1.55;

    :deep(a) {
      color: #5d8a24;
      font-weight: 600;
      text-decoration: none;

      &:hover { text-decoration: underline; }
    }

    :deep(code) {
      display: inline-block;
      background: #0f172a;
      padding: 0.3rem 0.65rem;
      margin: 0.18rem 0;
      border-radius: 0.3rem;
      font-size: 0.78rem;
      color: #94c840;
      font-family: 'SFMono-Regular', Consolas, monospace;
      border: 1px solid #1e293b;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }

    :deep(br + code) {
      margin-top: 0.35rem;
    }

    :deep(code::before) {
      content: '$ ';
      color: #64748b;
    }
  }
}
</style>
